/**
 * Download Manager - Handles YouTube audio downloads with progress tracking
 * Includes pre-checks and post-checks validation
 */

// Render deployment: download service runs on separate Render instance
const BACKEND_URL = 'https://dj-download-service.onrender.com';

export class DownloadManager {
  constructor(storage, audioContext) {
    this.storage = storage;
    this.audioContext = audioContext;
    this.downloads = new Map(); // Track in-progress downloads
  }

  /**
   * PRE-CHECKS: Validate search result before download
   */
  async preChecks(searchResult) {
    const checks = [];

    try {
      // Check 1: Valid search result structure
      if (!searchResult.sourceId || !searchResult.title) {
        checks.push({
          name: 'Valid Metadata',
          passed: false,
          message: 'Missing sourceId or title'
        });
        return checks;
      }
      checks.push({ name: 'Valid Metadata', passed: true, message: 'SourceId & title present' });

      // Check 2: Estimate duration
      if (!searchResult.duration || searchResult.duration < 30) {
        checks.push({
          name: 'Duration Valid',
          passed: false,
          message: `Duration ${searchResult.duration}s (min 30s required)`
        });
        return checks;
      }
      checks.push({ name: 'Duration Valid', passed: true, message: `${searchResult.duration}s` });

      // Check 3: Album art available (optional)
      const artAvailable = !!searchResult.imageUrl && searchResult.imageUrl.length > 0;
      checks.push({
        name: 'Album Art',
        passed: artAvailable,
        message: artAvailable ? 'Available' : 'Not found (optional)'
      });

      // Check 4: Search source is reliable
      const reliableSources = ['itunes', 'deezer', 'spotify', 'musicbrainz'];
      const sourceReliable = reliableSources.includes(searchResult.source?.toLowerCase());
      checks.push({
        name: 'Source Reliability',
        passed: sourceReliable,
        message: sourceReliable ? `${searchResult.source} (trusted)` : `${searchResult.source} (fallback)`
      });

      return checks;
    } catch (err) {
      checks.push({ name: 'Pre-Check Error', passed: false, message: err.message });
      return checks;
    }
  }

  /**
   * Convert search result to YouTube search query
   */
  getYouTubeSearchQuery(searchResult) {
    return `${searchResult.artist} ${searchResult.title}`.trim();
  }

  /**
   * Search YouTube for audio (fallback if no preview)
   */
  async searchYouTube(query) {
    try {
      // This would normally call a YouTube search API
      // For now, we construct a URL that the backend can use
      const searchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
      console.log(`🔍 YouTube search URL: ${searchUrl}`);

      // In a real implementation, you'd scrape or use yt-dlp to find the best match
      // For now, return the search URL and let the backend handle it
      return {
        query,
        searchUrl,
        estimated: true // Indicates this is an estimated result
      };
    } catch (err) {
      console.error('YouTube search failed:', err);
      throw err;
    }
  }

  /**
   * Main download function
   * Returns AudioBuffer on success, throws error on failure
   */
  async downloadTrackAudio(searchResult, onProgress, onStatusChange) {
    const trackId = searchResult.sourceId;

    try {
      onStatusChange?.('SEARCHING');

      // Check if already downloading
      if (this.downloads.has(trackId)) {
        throw new Error('Download already in progress');
      }

      // Pre-checks
      onStatusChange?.('PRE_CHECK');
      const preChecks = await this.preChecks(searchResult);
      const allChecksPassed = preChecks.every(c => c.passed);

      // Log pre-checks
      console.group(`📋 Pre-Checks: ${searchResult.title}`);
      preChecks.forEach(check => {
        const icon = check.passed ? '✓' : '✗';
        console.log(`${icon} ${check.name}: ${check.message}`);
      });
      console.groupEnd();

      if (!allChecksPassed) {
        // Some non-critical checks failed, but continue
        console.warn('⚠️ Some pre-checks failed, but continuing...');
      }

      // Primary: YouTube via yt-dlp (full-length track)
      // Fallback: iTunes preview (30-second clip — last resort only)
      onStatusChange?.('DOWNLOADING');
      let audioBlob;

      // Always try YouTube/yt-dlp first — this gives the full song
      const ytQuery = this.getYouTubeSearchQuery(searchResult);
      console.log(`🔍 Downloading full track via YouTube: "${ytQuery}"`);
      try {
        audioBlob = await this.requestBackendDownload(
          ytQuery,
          trackId,
          onProgress,
          searchResult.preview
        );
        console.log(`✓ Full track downloaded via yt-dlp`);
      } catch (ytErr) {
        console.warn(`⚠️ YouTube download failed: ${ytErr.message}`);

        // Last resort: try the 30-second preview if available
        if (searchResult.preview) {
          console.log(`🎵 Falling back to ${searchResult.source} preview (30s clip)...`);
          try {
            audioBlob = await this.requestDirectDownload(
              searchResult.preview,
              trackId,
              onProgress
            );
            console.warn(`⚠️ Using 30-second preview — full track unavailable`);
          } catch (previewErr) {
            // Both failed — throw the original YouTube error (more informative)
            throw ytErr;
          }
        } else {
          throw ytErr;
        }
      }

      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      onStatusChange?.('CONVERTING');
      const audioBuffer = await this.blobToAudioBuffer(audioBlob);

      // Post-checks
      onStatusChange?.('POST_CHECK');
      const postChecks = await this.postChecks(
        audioBuffer,
        searchResult
      );

      // Log post-checks
      console.group(`✔️ Post-Checks: ${searchResult.title}`);
      postChecks.forEach(check => {
        const icon = check.passed ? '✓' : '✗';
        console.log(`${icon} ${check.name}: ${check.message}`);
      });
      console.groupEnd();

      const allPostsPassed = postChecks.every(c => c.passed);
      if (!allPostsPassed) {
        console.warn('⚠️ Some post-checks failed, analyzing anyway...');
      }

      onStatusChange?.('COMPLETE');
      this.downloads.delete(trackId);

      return {
        audioBuffer,
        preChecks,
        postChecks,
        success: true
      };
    } catch (err) {
      console.error(`❌ Download failed for ${trackId}:`, err);
      this.downloads.delete(trackId);
      throw err;
    }
  }

  /**
   * Download audio directly from a preview URL (proxied through backend)
   * iTunes preview URLs have CORS restrictions on Apple's CDN,
   * so we proxy them through /api/download-preview to avoid browser CORS errors
   */
  async requestDirectDownload(previewUrl, trackId, onProgress) {
    try {
      // Route through backend proxy to avoid browser CORS restrictions on iTunes CDN
      const proxyUrl = `${BACKEND_URL}/api/download-preview?url=${encodeURIComponent(previewUrl)}`;
      const response = await fetch(proxyUrl, { method: 'GET' });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(err.error || `Preview proxy returned ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const reader = response.body.getReader();
      const chunks = [];
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.length;
        if (contentLength) {
          const pct = Math.round((loaded / contentLength) * 100);
          onProgress?.(pct);
        }
      }

      const blob = new Blob(chunks, { type: 'audio/mpeg' });
      return blob;
    } catch (err) {
      throw new Error(`Direct preview download failed: ${err.message}`);
    }
  }

  /**
   * Request download from backend with multiple fallback strategies
   */
  async requestBackendDownload(query, trackId, onProgress, previewUrl) {
    try {
      // Check backend health
      try {
        await fetch(`${BACKEND_URL}/api/health`);
      } catch {
        throw new Error('Download server unavailable - ensure server.js is running');
      }

      // Send download request - backend will search YouTube for the query
      const response = await fetch(`${BACKEND_URL}/api/download-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query, // Backend searches YouTube for this query
          videoId: trackId,
          previewUrl // Fallback preview URL if all strategies fail
        })
      });

      if (!response.ok) {
        let errorMsg = 'Unknown error';
        try {
          const error = await response.json();
          errorMsg = error.error || error.message || errorMsg;
          const tried = error.tried?.join(', ');
          if (tried) errorMsg += `\nTried: ${tried}`;
        } catch {
          errorMsg = `HTTP ${response.status}`;
        }
        throw new Error(`Backend error: ${errorMsg}`);
      }

      // Handle streaming response with progress
      const contentLength = response.headers.get('content-length');
      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        receivedLength += value.length;

        if (contentLength) {
          const progress = Math.round(
            (receivedLength / parseInt(contentLength, 10)) * 100
          );
          onProgress?.(progress);
        }
      }

      return new Blob(chunks, { type: 'audio/mpeg' });
    } catch (err) {
      console.error('Download request failed:', err);
      throw err;
    }
  }

  /**
   * Convert Blob to AudioBuffer
   */
  async blobToAudioBuffer(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
          resolve(audioBuffer);
        } catch (err) {
          reject(new Error(`Audio decode failed: ${err.message}`));
        }
      };

      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.readAsArrayBuffer(blob);
    });
  }

  /**
   * POST-CHECKS: Validate downloaded audio
   */
  async postChecks(audioBuffer, searchResult) {
    const checks = [];

    try {
      // Check 1: Buffer exists and has data
      if (!audioBuffer) {
        checks.push({ name: 'Audio Data', passed: false, message: 'No audio buffer' });
        return checks;
      }
      checks.push({ name: 'Audio Data', passed: true, message: 'Decoded successfully' });

      // Check 2: Sufficient duration
      const duration = audioBuffer.duration;
      const minDuration = 20; // 20 seconds minimum
      const durationValid = duration >= minDuration;
      checks.push({
        name: 'Duration Check',
        passed: durationValid,
        message: `${duration.toFixed(1)}s (min ${minDuration}s required)`
      });

      // Check 3: Audio channels
      const channelsValid = audioBuffer.numberOfChannels > 0;
      checks.push({
        name: 'Audio Channels',
        passed: channelsValid,
        message: `${audioBuffer.numberOfChannels} channel(s)`
      });

      // Check 4: Sample rate reasonable
      const sampleRateValid = audioBuffer.sampleRate >= 22050;
      checks.push({
        name: 'Sample Rate',
        passed: sampleRateValid,
        message: `${audioBuffer.sampleRate} Hz`
      });

      // Check 5: Compare duration with metadata
      if (searchResult.duration && Math.abs(duration - searchResult.duration) > 10) {
        checks.push({
          name: 'Duration Match',
          passed: false,
          message: `Metadata: ${searchResult.duration}s, Downloaded: ${duration.toFixed(1)}s`
        });
      } else {
        checks.push({
          name: 'Duration Match',
          passed: true,
          message: `Matches metadata (±10s tolerance)`
        });
      }

      // Check 6: RMS energy (signal presence)
      const rms = this.calculateRMS(audioBuffer);
      const hasSignal = rms > 0.001;
      checks.push({
        name: 'Signal Detection',
        passed: hasSignal,
        message: hasSignal ? `RMS: ${(rms * 1000).toFixed(2)}` : 'No audio signal'
      });

      return checks;
    } catch (err) {
      checks.push({ name: 'Post-Check Error', passed: false, message: err.message });
      return checks;
    }
  }

  /**
   * Calculate RMS (Root Mean Square) energy of audio
   */
  calculateRMS(audioBuffer) {
    const data = audioBuffer.getChannelData(0); // First channel
    let sum = 0;

    for (let i = 0; i < data.length; i++) {
      sum += data[i] * data[i];
    }

    return Math.sqrt(sum / data.length);
  }

  /**
   * Cancel in-progress download
   */
  cancelDownload(trackId) {
    this.downloads.delete(trackId);
  }
}

export default DownloadManager;
